import { isNil } from 'ramda';
import * as yup from 'yup';

import Certificate from 'src/types/resources/CertificateType';
import { ValidationError } from 'src/enums/Validation';
import { getValidFileTypeTest, VALID_FILE_SIZE } from 'src/forms/customRules';
import { getMaxFieldLengthErrorMessage } from 'src/utils/validation';
import { CERTIFICATE_VALID_FILE_TYPES, RESUME_VALID_FILE_TYPES } from 'src/utils/constants';
import { getFilePayload } from 'src/utils/filePayload';
import { CreateParams } from 'src/repositories/organizationAdmin/PurchaseOrderRepository';

import { DocumentFile } from './documents';

export type CertificateFormData = {
  documentName: string;
  schoolOrUniversity?: string;
  fieldOfStudy?: string;
  file?: DocumentFile | null;
};

const MAX_FIELD_LENGTH = 150;

export const getValidationFields = (isFormExtended = true) => ({
  documentName: yup
    .string()
    .trim()
    .max(MAX_FIELD_LENGTH, getMaxFieldLengthErrorMessage(MAX_FIELD_LENGTH))
    .required(ValidationError.default)
    .default(''),
  ...(isFormExtended && {
    schoolOrUniversity: yup
      .string()
      .trim()
      .max(MAX_FIELD_LENGTH, getMaxFieldLengthErrorMessage(MAX_FIELD_LENGTH))
      .nullable()
      .default(''),
    fieldOfStudy: yup
      .string()
      .trim()
      .max(MAX_FIELD_LENGTH, getMaxFieldLengthErrorMessage(MAX_FIELD_LENGTH))
      .nullable()
      .default(''),
  }),
  file: yup
    .object()
    .nullable()
    .shape({
      source: yup
        .mixed()
        .nullable()
        .test(getValidFileTypeTest(isFormExtended ? CERTIFICATE_VALID_FILE_TYPES : RESUME_VALID_FILE_TYPES))
        .test(VALID_FILE_SIZE)
        .when('url', {
          is: (value: string) => !isFormExtended && isNil(value),
          then: schema => schema.required(ValidationError.requiredFile),
        })
        .default(null),
      url: yup.string().nullable().default(null),
    }),
});

export const getValidationSchema = (isFormExtended = true) => yup.object(getValidationFields(isFormExtended));

export const initialValues = (certificate: Certificate | null, isFormExtended = true): CertificateFormData => {
  if (isNil(certificate)) {
    return getValidationSchema(isFormExtended).getDefault();
  }

  const { documentName, ...restDegree } = certificate;

  return {
    ...restDegree,
    file: {
      source: null,
      url: certificate.file,
      name: certificate.originalFileName,
    },
    documentName,
  };
};

export const attributesToSubmit = (values: CertificateFormData): CertificateFormData => {
  const { file, ...restValues } = values;
  const filePayload = getFilePayload(file, 'file') as Pick<CreateParams, 'fileName' | 'originalFileName'>;

  return {
    ...restValues,
    ...filePayload,
  };
};
